import React from 'react';
import {
  Box,
  Chip,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import BreakdownBars from './BreakdownBars';
import EmptyReportSection from './EmptyReportSection';
import MetricCard from './MetricCard';
import {
  FAILURE_TYPE_ORDER,
  SOURCE_ORDER,
  getOrderedEntries,
  getSortedCountEntries,
} from './aiReportUtils';
import { reportStyles } from './styles';

export default function FlowSummaryReport({ report = {}, flowName, testCaseName }) {
  const failureEntries = getOrderedEntries(report.failure_type_breakdown, FAILURE_TYPE_ORDER);
  const sourceEntries = getOrderedEntries(report.source_breakdown, SOURCE_ORDER);
  const eventEntries = getSortedCountEntries(report.events_affected);
  const failingAttributes = Array.isArray(report.failing_attributes)
    ? report.failing_attributes
    : [];

  const totalFailures = failureEntries.reduce((sum, [, count]) => sum + count, 0);
  const totalSourceIssues = sourceEntries.reduce((sum, [, count]) => sum + count, 0);
  const totalAffectedAttributes = eventEntries.reduce((sum, [, count]) => sum + count, 0);

  return (
    <Box>
      <Box sx={reportStyles.sectionHeader}>
        <Typography variant="h6" sx={reportStyles.sectionTitle}>
          {flowName || 'Flow'} AI Analysis
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {testCaseName ? `Test case ${testCaseName}` : 'AI-generated flow report'}
        </Typography>
      </Box>

      <Grid container spacing={2} sx={reportStyles.metricsGrid}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            label="Categorized Failures"
            value={totalFailures}
            helper="Failures grouped by type"
            tone={totalFailures > 0 ? 'error' : 'success'}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            label="Affected Events"
            value={eventEntries.length}
            helper={`${totalAffectedAttributes} failing attributes`}
            tone={eventEntries.length > 0 ? 'warning' : 'success'}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            label="Failing Attributes"
            value={failingAttributes.length}
            helper="Attributes requiring review"
            tone={failingAttributes.length > 0 ? 'error' : 'success'}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            label="Source Issues"
            value={totalSourceIssues}
            helper="mParticle and GMA coverage"
            tone={totalSourceIssues > 0 ? 'info' : 'success'}
          />
        </Grid>
      </Grid>

      <Divider sx={reportStyles.sectionDivider} />

      {report.plain_summary && (
        <Paper variant="outlined" sx={reportStyles.reportPaperWithMargin}>
          <Typography variant="subtitle1" sx={reportStyles.subsectionTitle}>
            Plain Summary
          </Typography>
          <Typography variant="h7" color="text.secondary" sx={{ mt: 1 }}>
            {report.plain_summary}
          </Typography>
        </Paper>
      )}

      <Grid container spacing={2} sx={reportStyles.metricsGrid}>
        <Grid item xs={12}>
          <Paper variant="outlined" sx={reportStyles.reportPaper}>
            <Typography variant="subtitle1" sx={reportStyles.subsectionTitle}>
              Failing Attributes
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={reportStyles.sectionDescription}>
              Expected values compared against mParticle and GMA.
            </Typography>

            {failingAttributes.length === 0 ? (
              <EmptyReportSection>No failing attributes reported.</EmptyReportSection>
            ) : (
              <TableContainer>
                <Table size="small" aria-label="Failing attributes">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={reportStyles.tableHeaderCell}>Event</TableCell>
                      <TableCell sx={reportStyles.tableHeaderCell}>Attribute</TableCell>
                      <TableCell sx={reportStyles.tableHeaderCell}>Expected</TableCell>
                      <TableCell sx={reportStyles.tableHeaderCell}>mParticle</TableCell>
                      <TableCell sx={reportStyles.tableHeaderCell}>GMA</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {failingAttributes.map((item, index) => (
                      <TableRow key={`${item.name || 'attribute'}-${index}`}>
                        <TableCell sx={reportStyles.wordBreakCell}>
                          {item.event || 'Unknown'}
                        </TableCell>
                        <TableCell sx={reportStyles.wordBreakCell}>
                          {item.name || 'Unknown'}
                        </TableCell>
                        <TableCell sx={reportStyles.wordBreakCell}>
                          {item.expected || 'N/A'}
                        </TableCell>
                        <TableCell sx={reportStyles.wordBreakCell}>
                          {item.mparticle_actual || 'N/A'}
                        </TableCell>
                        <TableCell sx={reportStyles.wordBreakCell}>
                          {item.gma_actual || 'N/A'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={reportStyles.metricsGrid}>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={reportStyles.reportPaper}>
            <Box sx={reportStyles.sectionToolbar}>
              <Box>
                <Typography variant="subtitle1" sx={reportStyles.subsectionTitle}>
                  Source Breakdown
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Which downstream sources show the issue.
                </Typography>
              </Box>
              <Chip
                label={`${totalSourceIssues} total`}
                size="small"
                color={totalSourceIssues > 0 ? 'info' : 'success'}
                variant="outlined"
              />
            </Box>
            <BreakdownBars entries={sourceEntries} total={totalSourceIssues} color="info" />
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={reportStyles.reportPaper}>
            <Typography variant="subtitle1" sx={reportStyles.subsectionTitle}>
              Events Affected
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={reportStyles.sectionDescription}>
              Event names and the number of failing attributes found.
            </Typography>

            {eventEntries.length === 0 ? (
              <EmptyReportSection>No affected events reported.</EmptyReportSection>
            ) : (
              <TableContainer>
                <Table size="small" aria-label="Events affected">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={reportStyles.tableHeaderCell}>Event</TableCell>
                      <TableCell align="right" sx={reportStyles.tableHeaderCell}>
                        Failing Attributes
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {eventEntries.map(([eventName, count]) => (
                      <TableRow key={eventName}>
                        <TableCell sx={reportStyles.wordBreakCell}>{eventName}</TableCell>
                        <TableCell align="right">{count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
